import { useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import type { Decision } from "../../api/access/decisions";
import ErrorNotice from "../../errors/ErrorNotice";
import { useSubmission } from "../../shared/state/useSubmission";
import AppButton from "../buttons/AppButton";
import BlockedState from "../feedback/BlockedState";
import { FORM_CONTROL_CLASS } from "../styles/formStyles";
import Field from "./Field";

interface ReasonedChangeFormProps<T> {
  value: T;
  /** The served decision on changing it (accounts.md GS-8); none where only those who may see it. Allowed, the fields
   * are editable and a change starts the draft; refused, they are read-only, and BlockedState says what is missing. */
  requires?: Decision;
  /** Draws the fields; without `onChange` they are read-only. */
  children: (value: T, onChange?: (value: T) => void) => ReactNode;
  /** Saves the draft with its reason; the form closes once it succeeds. */
  onSave: (draft: T, reason: string) => Promise<void>;
}

/**
 * Settings that change only with a reason, as the permission history records them
 * (accounts.md ST-1): for whoever may change them the fields are editable as they stand, and
 * the first change starts the draft with its required reason, Save and Cancel (Sol critique C7).
 */
export default function ReasonedChangeForm<T>({ value, requires, children, onSave }: ReasonedChangeFormProps<T>) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<T | null>(null);
  const [reason, setReason] = useState("");
  const { state, submit, reset } = useSubmission();
  const pending = state.status === "submitting";

  if (draft === null) {
    return (
      <div className="space-y-3">
        {children(value, requires === undefined || requires.refusal === null ? setDraft : undefined)}
        {requires === undefined ? null : <BlockedState decision={requires} />}
      </div>
    );
  }
  const close = () => {
    setDraft(null);
    setReason("");
  };
  const cancel = () => {
    close();
    // A failure is forgotten with the draft; only a failed submission can be reset.
    if (state.status === "failed") reset();
  };
  return (
    <form
      className="space-y-3"
      onSubmit={(event) => {
        event.preventDefault();
        submit(async () => {
          await onSave(draft, reason.trim());
          close();
        });
      }}
    >
      {children(draft, setDraft)}
      <Field label={t("common.settings.reason")} hint={t("common.settings.reasonHint")}>
        {(control) => (
          <textarea {...control} className={FORM_CONTROL_CLASS} rows={2} value={reason} required
            onChange={(event) => { setReason(event.target.value); }} />
        )}
      </Field>
      {state.status === "failed" ? <ErrorNotice error={state.error} /> : null}
      <div className="flex gap-2">
        <AppButton type="submit" variant="primary" size="sm" disabled={pending || reason.trim() === ""}>{t("common.settings.save")}</AppButton>
        <AppButton variant="secondary" size="sm" disabled={pending} onClick={cancel}>{t("common.cancel")}</AppButton>
      </div>
    </form>
  );
}
